import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, EyeOff, Sparkles, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getUnmappedRevenueCodes, postUnmappedRevenueAction } from '../../utils/api';
import { InlineSelect } from '../InlineTable';
import ImportReviewModal from './ImportReviewModal';
import { ConfigToolbar, ToolbarButton, ConfigEmptyState } from '../ConfigPages';
import { EtlBanner, EtlLoading, FilterSelect } from '../EtlPages';

// UnmappedCodesSection — Phase 1 of the Unmapped Revenue Watch.
// Triage queue for revenueTypes rows with a blank mappedType. Revenue booked
// against these codes exists in the database but is excluded from every
// report until someone maps the code or ignores it (mappedType='DNI').
//
// Editing follows the Revenue Mapping tables: the mapped-type and
// classification selects sit directly in the row (InlineTable kit), so
// mapping a code is one dropdown pick. Picking a mapped type applies it
// immediately and the row leaves the queue; classification staged beforehand
// rides along. New types are created from the select's search box, same as
// Chart of Accounts groups.

function formatMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function formatDateOnly(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toISOString().slice(0, 10) : '—';
}

export default function UnmappedCodesSection({ currentOrganization, accessibleHotelIds, locations, scopeReady = true, onCountChange }) {
  const orgId = currentOrganization?.id || currentOrganization?.orgId || null;

  const [rows, setRows] = useState([]);
  const [knownMappedTypesByLocation, setKnownMappedTypesByLocation] = useState({});
  const [typeClassifications, setTypeClassifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busyIds, setBusyIds] = useState(new Set());          // rows with an in-flight action
  const [stagedClass, setStagedClass] = useState({});         // revenueTypeId -> typeClassificationId staged before mapping
  const [createdTypes, setCreatedTypes] = useState({});       // locationId -> [types created this session]
  const [fileReviewRow, setFileReviewRow] = useState(null);   // row whose source file is open in ImportReviewModal

  const loadRequestId = useRef(0);
  const loadList = useCallback(async () => {
    // Wait for the scope to settle, then let only the newest response land.
    if (!scopeReady) return;
    const requestId = ++loadRequestId.current;
    if (!orgId && (!accessibleHotelIds || accessibleHotelIds.length === 0)) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getUnmappedRevenueCodes(orgId, { hotelIds: accessibleHotelIds });
      if (loadRequestId.current !== requestId) return;
      setRows(data?.rows || []);
      setKnownMappedTypesByLocation(data?.knownMappedTypesByLocation || {});
      setTypeClassifications(data?.typeClassifications || []);
      setSelectedIds(new Set());
      setStagedClass({});
    } catch (err) {
      if (loadRequestId.current !== requestId) return;
      setError(err.message || 'Failed to load unmapped codes');
    } finally {
      if (loadRequestId.current === requestId) setLoading(false);
    }
  }, [orgId, accessibleHotelIds, scopeReady]);

  // Feeds the tab badge.
  useEffect(() => { if (onCountChange) onCountChange(rows.length); }, [rows.length, onCountChange]);

  useEffect(() => { loadList(); }, [loadList]);

  const locationName = useCallback((locationId) => {
    const loc = (locations || []).find((l) => Number(l.id) === Number(locationId));
    return loc?.name || `#${locationId}`;
  }, [locations]);

  const locationOptions = useMemo(() => {
    const ids = [...new Set(rows.map((r) => r.locationId))];
    return ids
      .map((id) => ({ id, name: locationName(id) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, locationName]);

  const classificationOptions = useMemo(
    () => typeClassifications.map((tc) => ({ value: tc.id, label: tc.name })),
    [typeClassifications]
  );

  const typeOptionsForLocation = useCallback((locationId) => {
    const base = knownMappedTypesByLocation[locationId] || [];
    const created = createdTypes[locationId] || [];
    const all = [...new Set([...base, ...created])].sort((a, b) => a.localeCompare(b));
    return all.map((t) => ({ value: t, label: t }));
  }, [knownMappedTypesByLocation, createdTypes]);

  const filteredRows = useMemo(() => {
    let out = rows;
    if (locationFilter) out = out.filter((r) => String(r.locationId) === String(locationFilter));
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      out = out.filter((r) =>
        (r.codeName || '').toLowerCase().includes(q)
        || (r.displayName || '').toLowerCase().includes(q)
        || locationName(r.locationId).toLowerCase().includes(q));
    }
    return out;
  }, [rows, locationFilter, searchTerm, locationName]);

  const selectableRows = filteredRows;
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selectedIds.has(r.revenueTypeId));
  const selectedRows = useMemo(
    () => rows.filter((r) => selectedIds.has(r.revenueTypeId)),
    [rows, selectedIds]
  );
  const selectedWithSuggestion = selectedRows.filter((r) => r.suggestion && r.suggestion.mappedType !== 'DNI');

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (allSelected) return new Set();
      const next = new Set(prev);
      selectableRows.forEach((r) => next.add(r.revenueTypeId));
      return next;
    });
  };

  const toggleOne = (revenueTypeId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(revenueTypeId)) next.delete(revenueTypeId);
      else next.add(revenueTypeId);
      return next;
    });
  };

  // The API caps a single request at 200 items; chunk client-side so bulk
  // actions on any selection size just work. Chunks run sequentially — each
  // one is itself a batch, and sequencing keeps the SQL load bounded.
  const ACTION_CHUNK_SIZE = 200;

  const runAction = useCallback(async (action, items, successMessage) => {
    const ids = items.map((i) => i.revenueTypeId);
    setBusyIds((prev) => new Set([...prev, ...ids]));
    try {
      let applied = 0;
      let failed = 0;
      let firstError = null;
      const appliedIds = new Set();
      for (let i = 0; i < items.length; i += ACTION_CHUNK_SIZE) {
        const chunk = items.slice(i, i + ACTION_CHUNK_SIZE);
        const result = await postUnmappedRevenueAction(action, chunk);
        applied += result?.applied ?? 0;
        failed += result?.failed ?? 0;
        if (!firstError) firstError = (result?.results || []).find((r) => !r.ok)?.error || null;
        for (const r of result?.results || []) {
          if (r.ok) appliedIds.add(r.revenueTypeId);
        }
        // Shrink the queue as each chunk lands so large bulk runs show progress.
        setRows((prev) => prev.filter((row) => !appliedIds.has(row.revenueTypeId)));
        setSelectedIds((prev) => new Set([...prev].filter((id) => !appliedIds.has(id))));
      }
      if (failed > 0) {
        toast.error(`${applied} applied, ${failed} failed${firstError ? ` — ${firstError}` : ''}`);
      } else {
        toast.success(successMessage(applied));
      }
    } catch (err) {
      toast.error(err.message || `Failed to ${action}`);
    } finally {
      setBusyIds((prev) => new Set([...prev].filter((id) => !ids.includes(id))));
    }
  }, []);

  const mapRow = useCallback((row, mappedType) => {
    if (!mappedType) return;
    runAction(
      'map',
      [{
        revenueTypeId: row.revenueTypeId,
        mappedType,
        typeClassificationId: stagedClass[row.revenueTypeId] ?? null,
      }],
      () => `Mapped "${row.codeName}" → ${mappedType}`
    );
  }, [runAction, stagedClass]);

  const acceptSuggestion = useCallback((row) => {
    if (row.suggestion.mappedType === 'DNI') {
      runAction('ignore', [{ revenueTypeId: row.revenueTypeId }], () => `Ignored "${row.codeName}" (DNI)`);
      return;
    }
    runAction(
      'accept_suggestion',
      [{
        revenueTypeId: row.revenueTypeId,
        mappedType: row.suggestion.mappedType,
        typeClassificationId: stagedClass[row.revenueTypeId] ?? row.suggestion.typeClassificationId,
        suggestionConfidence: row.suggestion.confidence,
      }],
      () => `Mapped "${row.codeName}" → ${row.suggestion.mappedType}`
    );
  }, [runAction, stagedClass]);

  const ignoreRows = (targets) => runAction(
    'ignore',
    targets.map((r) => ({ revenueTypeId: r.revenueTypeId })),
    (n) => `${n} code${n === 1 ? '' : 's'} ignored (DNI)`
  );

  const acceptSelectedSuggestions = () => runAction(
    'accept_suggestion',
    selectedWithSuggestion.map((r) => ({
      revenueTypeId: r.revenueTypeId,
      mappedType: r.suggestion.mappedType,
      typeClassificationId: stagedClass[r.revenueTypeId] ?? r.suggestion.typeClassificationId,
      suggestionConfidence: r.suggestion.confidence,
    })),
    (n) => `${n} suggestion${n === 1 ? '' : 's'} accepted`
  );

  const totalUnreported30d = useMemo(
    () => filteredRows.reduce((sum, r) => sum + Math.abs(Number(r.amount30d) || 0), 0),
    [filteredRows]
  );

  return (
    <div className="it-frame">
      <ConfigToolbar
        search={{ value: searchTerm, onChange: setSearchTerm, placeholder: 'Search code or hotel…' }}
        meta={filteredRows.length > 0
          ? `${formatMoney(totalUnreported30d)} excluded from reports over 30 days`
          : null}
        actions={(
          <ToolbarButton iconOnly icon={RefreshCw} onClick={loadList} disabled={loading} aria-label="Refresh" title="Refresh" />
        )}
      >
        <FilterSelect
          value={locationFilter || null}
          options={locationOptions.map((o) => ({ value: String(o.id), label: o.name }))}
          onChange={(v) => setLocationFilter(v || '')}
          placeholder="All hotels"
          ariaLabel="Filter by hotel"
        />
      </ConfigToolbar>

      {/* Bulk actions. Both are durable decisions, so they say what they write. */}
      {selectedIds.size > 0 && (
        <div className="cp-toolbar" style={{ background: 'var(--lodge-orange-50)' }}>
          <span className="cp-meta" style={{ color: 'var(--lodge-orange-600)' }}>{selectedIds.size} selected</span>
          <ToolbarButton
            variant="ai"
            icon={Sparkles}
            onClick={acceptSelectedSuggestions}
            disabled={selectedWithSuggestion.length === 0}
            title="Applies each row's precedent suggestion; rows without one are skipped"
          >
            Accept suggestions ({selectedWithSuggestion.length})
          </ToolbarButton>
          <ToolbarButton
            icon={EyeOff}
            onClick={() => ignoreRows(selectedRows)}
            title="Sets mappedType='DNI' — permanently excluded from reports, never re-triaged"
          >
            Ignore ({selectedIds.size})
          </ToolbarButton>
          <div className="cp-toolbar-spacer" />
          <ToolbarButton variant="ghost" onClick={() => setSelectedIds(new Set())}>Clear selection</ToolbarButton>
        </div>
      )}

      {error && (
        <div style={{ padding: 14 }}>
          <EtlBanner tone="error" title="Could not load unmapped codes">{error}</EtlBanner>
        </div>
      )}

      {/* Table */}
      <div className="it-scroll" style={{ '--it-max-h': '60vh' }}>
        <table className="it-tbl">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all" />
              </th>
              <th>Hotel</th>
              <th>Revenue code</th>
              <th style={{ textAlign: 'right' }}>30d unreported</th>
              <th style={{ textAlign: 'right' }}>% of rev</th>
              <th>Last activity</th>
              <th>Maps to</th>
              <th>Classification</th>
              <th aria-label="Actions" style={{ width: 84 }} />
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={9}><EtlLoading>Loading unmapped codes…</EtlLoading></td></tr>
            )}
            {!loading && filteredRows.length === 0 && (
              <tr><td colSpan={9}>
                <ConfigEmptyState
                  title="Nothing unmapped"
                  body="Every revenue code with recent activity reaches reports."
                />
              </td></tr>
            )}
            {filteredRows.map((row) => {
              const busy = busyIds.has(row.revenueTypeId);
              const suggestion = row.suggestion;
              const hasMapSuggestion = suggestion && suggestion.mappedType !== 'DNI';
              const strong = hasMapSuggestion && suggestion.locationCount >= 3 && suggestion.confidence >= 0.8;
              return (
                <tr
                  key={row.revenueTypeId}
                  className={`cp-row it-row-hover${selectedIds.has(row.revenueTypeId) ? ' it-row-selected' : ''}`}
                  style={busy ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
                >
                  <td className="cp-cell-tight">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.revenueTypeId)}
                      onChange={() => toggleOne(row.revenueTypeId)}
                      aria-label={`Select ${row.codeName}`}
                    />
                  </td>
                  <td className="cp-cell"><span className="cp-name">{locationName(row.locationId)}</span></td>
                  <td className="cp-cell">
                    <div className="cp-mono" style={{ color: 'var(--lodge-onyx-900)', fontWeight: 500 }}>{row.codeName}</div>
                    {row.displayName && row.displayName !== row.codeName && (
                      <div className="cp-code">{row.displayName}</div>
                    )}
                  </td>
                  <td className="cp-cell" style={{ textAlign: 'right' }}>
                    <span className="cp-mono" style={{ color: 'var(--color-error-ink)', fontWeight: 500 }}>{formatMoney(row.amount30d)}</span>
                  </td>
                  <td className="cp-cell" style={{ textAlign: 'right' }}>
                    <span className="cp-mono">{row.pctOfLocation30d == null ? '—' : `${Number(row.pctOfLocation30d).toFixed(1)}%`}</span>
                  </td>
                  <td className="cp-cell">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className="cp-mono">{formatDateOnly(row.lastSeen)}</span>
                      {row.sourceFile && (
                        <ToolbarButton
                          small iconOnly variant="ghost" icon={FileText}
                          onClick={() => setFileReviewRow(row)}
                          title={`Open the source file (${row.sourceFile.fileType || 'import'}, ${formatDateOnly(row.sourceFile.effectiveDate)}) — is this real revenue, or a totals/stat line?`}
                          aria-label={`View source file for ${row.codeName}`}
                        />
                      )}
                    </div>
                  </td>
                  <td className="cp-cell-tight" style={{ minWidth: 200 }}>
                    <div className="flex items-center gap-1.5">
                      <div className="flex-1 min-w-0">
                        <InlineSelect
                          value={null}
                          options={typeOptionsForLocation(row.locationId)}
                          onChange={(v) => mapRow(row, v)}
                          onCreateOption={(name) => {
                            const trimmed = String(name).trim();
                            setCreatedTypes((prev) => ({
                              ...prev,
                              [row.locationId]: [...(prev[row.locationId] || []), trimmed],
                            }));
                            return trimmed;
                          }}
                          createLabel="New type"
                          placeholder="Map to…"
                          rowActive
                          disabled={busy}
                          clearable={false}
                          minWidth={220}
                          ariaLabel={`Mapped type for ${row.codeName}`}
                        />
                      </div>
                      {/* A precedent chip: how the other properties already mapped
                          this same code name. `strong` means broad agreement. */}
                      {hasMapSuggestion && (
                        <button
                          type="button"
                          onClick={() => acceptSuggestion(row)}
                          disabled={busy}
                          className={`cp-tag${strong ? ' cp-ok' : ' cp-info'}`}
                          style={{ cursor: 'pointer', gap: 4 }}
                          title={`${suggestion.locationCount} of ${suggestion.totalLocationCount} mapped locations use "${suggestion.mappedType}" — click to apply`}
                        >
                          <Sparkles size={10} />
                          {suggestion.mappedType} · {suggestion.locationCount}
                        </button>
                      )}
                      {suggestion && suggestion.mappedType === 'DNI' && (
                        <button
                          type="button"
                          onClick={() => acceptSuggestion(row)}
                          disabled={busy}
                          className="cp-tag"
                          style={{ cursor: 'pointer', gap: 4 }}
                          title={`${suggestion.locationCount} of ${suggestion.totalLocationCount} mapped locations ignore this code — click to ignore it here too`}
                        >
                          <EyeOff size={10} />
                          Ignored · {suggestion.locationCount}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="cp-cell-tight" style={{ minWidth: 180 }}>
                    <InlineSelect
                      value={stagedClass[row.revenueTypeId] ?? null}
                      options={classificationOptions}
                      onChange={(v) => setStagedClass((prev) => ({ ...prev, [row.revenueTypeId]: v }))}
                      placeholder="Classification…"
                      rowActive
                      disabled={busy}
                      minWidth={200}
                      ariaLabel={`Classification for ${row.codeName}`}
                    />
                  </td>
                  <td className="cp-cell-tight" style={{ textAlign: 'right' }}>
                    <ToolbarButton
                      small
                      onClick={() => ignoreRows([row])}
                      disabled={busy}
                      title="Sets mappedType='DNI' — permanently excluded from reports"
                    >
                      Ignore
                    </ToolbarButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="cp-help" style={{ padding: '12px 14px', margin: 0, borderTop: '1px solid var(--lodge-onyx-100)' }}>
        Revenue on these codes is stored but excluded from all reports until mapped. Pick a mapped type to resolve a
        code — set the classification first if you want it saved with the mapping. “Ignore” marks a code DNI
        (do-not-include), a durable decision that survives future imports. Suggestion chips show how other hotels
        mapped the same code name — click one to apply it. The file icon opens the source document behind the code's
        most recent file-based activity (codes from direct API feeds have no file). Every action is recorded in the
        mapping audit trail.
      </p>

      {/* Source-file review — reuses the DLQ Import Review modal (source file
          alongside imported data) so the operator can judge whether the code
          is real revenue or a totals/stat line before mapping it. */}
      {fileReviewRow && fileReviewRow.sourceFile && (
        <ImportReviewModal
          detail={{
            state: {
              id: fileReviewRow.sourceFile.stateId,
              orgId: fileReviewRow.orgId,
              locationId: fileReviewRow.locationId,
              fileType: fileReviewRow.sourceFile.fileType || 'import',
              effectiveDate: fileReviewRow.sourceFile.effectiveDate,
              importId: fileReviewRow.sourceFile.importId,
              imports_nightAuditPath: fileReviewRow.sourceFile.nightAuditPath,
              archivedBlobPath: fileReviewRow.sourceFile.archivedBlobPath,
              sourceBlobPath: fileReviewRow.sourceFile.sourceBlobPath,
            },
          }}
          locationName={locationName(fileReviewRow.locationId)}
          onClose={() => setFileReviewRow(null)}
        />
      )}
    </div>
  );
}
